import { exactMoney } from "@/lib/exactMoney";
export { exactMoney } from "@/lib/exactMoney";
import { useEffect, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AuditAreas(props) {return <EngagementAreas key={props.id} {...props}/>;}
function EngagementAreas({ id }) {
  return (
    <Tabs defaultValue="areas">
      <TabsList className="mb-3">
        <TabsTrigger value="areas" data-testid="aa-tab-areas">Area Audit</TabsTrigger>
        <TabsTrigger value="matrix" data-testid="aa-tab-matrix">Matriks Risiko → Prosedur</TabsTrigger>
      </TabsList>
      <TabsContent value="areas"><Areas id={id} /></TabsContent>
      <TabsContent value="matrix"><Matrix id={id} /></TabsContent>
    </Tabs>
  );
}

export function Areas({ id }) {
  const [rows, setRows] = useState([]);
  const [wps,setWps]=useState([]);
  const [meta, setMeta] = useState({ conclusions: [], statuses: [], risk_ratings: [] });
  const load = () => api.get(`/engagements/${id}/audit-areas`).then((r) => setRows(r.data));
  useEffect(() => {
    let active=true;
    Promise.all([api.get(`/engagements/${id}/audit-areas`),api.get('/meta/audit-areas'),api.get(`/engagements/${id}/ewps`)]).then(([a,m,w])=>{if(active){setRows(a.data);setMeta(m.data);setWps(w.data);}}).catch(e=>toast.error(getErrorMessage(e)));
    return()=>{active=false;};
  }, [id]);
  const save = async (a, patch) => {
    try { await api.put(`/engagements/${id}/audit-areas/${encodeURIComponent(a.area_key)}`, patch);
    toast.success("Area disimpan"); await load(); } catch(e) {toast.error(getErrorMessage(e));}
  };
  return (
    <div className="bg-white border border-border rounded-sm overflow-x-auto">
      <p className="text-xs p-2">Saldo bruto absolut akun untuk perencanaan. Kesimpulan area mengikuti KKA tertaut yang selesai dan direviu pada sumber saat ini.</p>
      <table className="w-full audit-table">
        <thead><tr>
          <th>Area / FS Line</th><th>Akun</th><th className="text-right">Saldo TB</th><th className="text-right">Saldo TL</th>
          <th className="text-right">Pergerakan</th><th className="text-right">% PM</th><th>Material</th>
          <th>Rating Risiko</th><th>Asersi Utama</th><th>Respons Terencana</th><th>Status</th><th>Kesimpulan</th>
        </tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.area_key} className={a.material ? "bg-amber-50/40" : ""} data-testid={`area-row-${a.area_key}`}>
              <td className="font-medium text-xs">{a.area_key}<div className="text-[10px] text-muted-foreground">{a.account_names.join(", ")}</div></td>
              <td className="tabular text-center">{a.accounts}</td>
              <td className="tabular text-right">{exactMoney(a.cy_balance_exact??a.cy_balance)}</td>
              <td className="tabular text-right text-muted-foreground">{exactMoney(a.py_balance_exact??a.py_balance)}</td>
              <td className="tabular text-right">{exactMoney(a.movement_exact??a.movement)}</td>
              <td className="tabular text-right">{a.pct_pm==null?"Belum tersedia":`${a.pct_pm}%`}</td>
              <td>{a.material==null ? "Belum dinilai" : a.material ? <span className="text-[10px] px-1.5 py-0.5 border rounded-sm bg-amber-100 text-amber-800 border-amber-300">Ya</span> : <span className="text-[10px] text-muted-foreground">-</span>}</td>
              <td>
                <Select value={a.risk_rating || ""} onValueChange={(v) => save(a, { risk_rating: v })}>
                  <SelectTrigger className="h-7 text-xs w-24" data-testid={`area-risk-${a.area_key}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{(meta.risk_ratings || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td><Input className="h-7 text-xs w-32" placeholder="asersi" defaultValue={a.assertions} onBlur={(e) => e.target.value !== a.assertions && save(a, { assertions: e.target.value })} data-testid={`area-assert-${a.area_key}`} /></td>
              <td><Input className="h-7 text-xs w-40" placeholder="respons/prosedur" defaultValue={a.planned_response} onBlur={(e) => e.target.value !== a.planned_response && save(a, { planned_response: e.target.value })} data-testid={`area-resp-${a.area_key}`} /></td>
              <td data-testid={`area-status-${a.area_key}`}>{a.status}<ul className="text-[10px] text-amber-800">{(a.data_issues||[]).map((issue,i)=><li key={i}>{issue}</li>)}</ul></td>
              <td><div className="text-xs">{a.conclusion||'Belum ada kesimpulan direviu'}</div><select aria-label={`KKA kesimpulan ${a.area_key}`} value={a.conclusion_wp_id||''} onChange={e=>save(a,{conclusion_wp_id:e.target.value})} className="border text-xs"><option value="">Pilih KKA area</option>{wps.filter(w=>w.audit_area===a.area_key).map(w=><option key={w.id} value={w.id}>{w.wp_ref} — {w.wp_title}</option>)}</select></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Matrix({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/engagements/${id}/risk-matrix`).then((r) => setD(r.data)); }, [id]);
  if (!d) return null;
  const STY = { "Fully Addressed": "bg-green-100 text-green-700 border-green-300", "Partially Addressed": "bg-amber-100 text-amber-800 border-amber-300", "Unaddressed": "bg-red-100 text-red-700 border-red-300" };
  const LAB = { "Fully Addressed": "Ditangani Penuh", "Partially Addressed": "Ditangani Sebagian", "Unaddressed": "Belum Ditangani" };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="matrix-summary">
        {[["Total Risiko", d.total], ["Ditangani Penuh", d.fully], ["Ditangani Sebagian", d.partially], ["Belum Ditangani", d.unaddressed], ["Signifikan Belum Selesai", d.significant_incomplete??d.significant_unaddressed]].map(([l, v], i) => (
          <div key={l} className={`bg-white border rounded-sm p-2 ${i === 4 && v > 0 ? "border-red-300" : "border-border"}`}>
            <div className={`text-base font-head font-bold tabular ${i === 4 && v > 0 ? "text-red-600" : ""}`}>{v}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto"><table className="w-full audit-table">
        <thead><tr><th>Ref</th><th>Risiko</th><th>Akun</th><th>Asersi</th><th>Signifikan</th><th className="text-center">Prosedur</th><th className="text-center">Respons dengan pelaksanaan direviu</th><th>Status</th></tr></thead>
        <tbody>{d.matrix.map((r) => (
          <tr key={r.ref} data-testid={`matrix-row-${r.ref}`}>
            <td className="font-mono text-xs">{r.ref}</td><td className="text-xs">{r.description}</td>
            <td className="text-xs">{r.account}</td><td className="text-xs">{r.assertion}</td>
            <td>{r.significant ? <span className="text-[10px] px-1 border rounded-sm bg-red-50 text-red-600 border-red-200">Sig</span> : "-"}{r.fraud ? <span className="text-[10px] px-1 ml-1 border rounded-sm bg-orange-50 text-orange-600 border-orange-200">Fraud</span> : ""}</td>
            <td className="text-center tabular">{r.procedures}</td><td className="text-center tabular">{r.with_conclusion}</td>
            <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${STY[r.status]}`}>{LAB[r.status]}</span></td>
          </tr>
        ))}
          {d.matrix.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-4">Belum ada risiko.</td></tr>}
        </tbody>
      </table></div>
    </div>
  );
}
